import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useContent } from "@/lib/content-context";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import ImagePicker from "@/components/admin/ImagePicker";
import { TextField, TextArea, SaveBar, SectionCard, PageHeader } from "@/components/admin/Form";

export default function HomeEditor() {
    const { content, refresh } = useContent();
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (content) {
            const copy = JSON.parse(JSON.stringify(content));
            if (!copy.brand) copy.brand = { logo_url: "" };
            setData(copy);
        }
    }, [content]);
    if (!data) return null;

    const set = (path, value) => {
        setData((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let cur = next;
            for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
            cur[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const saveSection = async (section) => {
        setSaving(true);
        try {
            await api.put(`/content/${section}`, { data: data[section] });
            await refresh();
            toast.success("Saved.");
        } catch (e) {
            toast.error("Save failed");
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const saveAll = async () => {
        setSaving(true);
        try {
            const payload = {
                brand: data.brand,
                hero: data.hero,
                story: data.story,
                coffee: data.coffee,
                food: data.food,
                community: data.community,
                instagram: data.instagram,
                footer: data.footer,
            };
            await api.put("/content", { data: payload });
            await refresh();
            toast.success("All home content saved.");
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="home-editor">
            <PageHeader subtitle="Home Page" title="Edit home content" />

            {/* BRAND / LOGO */}
            <SectionCard title="Brand Logo" description="The circular logo in the center of the navbar. Upload your own PNG/SVG to replace the built-in mark.">
                <ImagePicker
                    label="Logo Image (square — PNG, JPG, or SVG)"
                    accept="image/*"
                    value={data.brand?.logo_url}
                    onChange={(v) => set("brand.logo_url", v)}
                    testid="brand-logo-picker"
                    aspect="aspect-square"
                    hint="Recommended: 400 × 400 px · transparent PNG · under 1 MB"
                />
                <p className="text-coffee/60 text-xs">
                    Leave empty to use the built-in Cafe Hideout logo (circular cup mark). Recommended: 400×400 transparent PNG.
                </p>
            </SectionCard>

            {/* HERO */}
            <SectionCard title="Hero" description="Full-screen brand video and strapline.">
                <ImagePicker
                    label="Hero Video (MP4)"
                    accept="video/*"
                    value={data.hero.video_url}
                    onChange={(v) => set("hero.video_url", v)}
                    testid="hero-video-picker"
                    aspect="aspect-video"
                    hint="Recommended: 1920 × 1080 px (16:9) · MP4 · under 10 MB · 10-30 seconds loop"
                />
                <ImagePicker
                    label="Video Poster Image (fallback)"
                    value={data.hero.poster_url}
                    onChange={(v) => set("hero.poster_url", v)}
                    testid="hero-poster-picker"
                    hint="Recommended: 1920 × 1080 px (16:9) · JPG · under 500 KB"
                />
                <TextField
                    label="Strapline"
                    value={data.hero.strapline}
                    onChange={(v) => set("hero.strapline", v)}
                    testid="hero-strapline-input"
                />
            </SectionCard>

            {/* STORY */}
            <SectionCard title="The Hideout Story" description="Pull-quote + image + paragraphs + food cards row.">
                <TextField
                    label="Pull-Quote"
                    value={data.story.pull_quote}
                    onChange={(v) => set("story.pull_quote", v)}
                    testid="story-pullquote-input"
                />
                <ImagePicker
                    label="Story Image (left side of paragraphs)"
                    value={data.story.image}
                    onChange={(v) => set("story.image", v)}
                    testid="story-image-picker"
                    aspect="aspect-[4/5]"
                    hint="Recommended: 800 × 1000 px (4:5 portrait) · JPG/PNG · under 500 KB"
                />
                {data.story.paragraphs.map((p, i) => (
                    <div key={i} className="flex gap-2 items-start">
                        <TextArea
                            label={`Paragraph ${i + 1}`}
                            value={p}
                            onChange={(v) => {
                                const arr = [...data.story.paragraphs];
                                arr[i] = v;
                                set("story.paragraphs", arr);
                            }}
                            testid={`story-paragraph-${i}`}
                        />
                        <button
                            onClick={() => {
                                const arr = data.story.paragraphs.filter((_, idx) => idx !== i);
                                set("story.paragraphs", arr);
                            }}
                            className="mt-8 text-coffee/50 hover:text-destructive"
                            aria-label="remove paragraph"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}
                <button
                    onClick={() => set("story.paragraphs", [...data.story.paragraphs, ""])}
                    className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline"
                >
                    <Plus size={14} /> Add Paragraph
                </button>
                <TextField label="CTA Label" value={data.story.cta_label} onChange={(v) => set("story.cta_label", v)} testid="story-cta-label" />
                <TextField label="CTA Link" value={data.story.cta_link} onChange={(v) => set("story.cta_link", v)} testid="story-cta-link" />

                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Food Cards Row</div>
                    {data.story.food_cards?.map((c, i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 p-4 border border-coffee/10">
                            <ImagePicker value={c.image} onChange={(v) => {
                                const arr = [...data.story.food_cards]; arr[i] = { ...arr[i], image: v };
                                set("story.food_cards", arr);
                            }} testid={`story-card-${i}-image`} hint="Recommended: 800 × 1000 px (4:5 portrait) · JPG · under 400 KB" />
                            <div className="space-y-4">
                                <TextField label="Label" value={c.label} onChange={(v) => {
                                    const arr = [...data.story.food_cards]; arr[i] = { ...arr[i], label: v };
                                    set("story.food_cards", arr);
                                }} testid={`story-card-${i}-label`} />
                                <TextField label="Title" value={c.title} onChange={(v) => {
                                    const arr = [...data.story.food_cards]; arr[i] = { ...arr[i], title: v };
                                    set("story.food_cards", arr);
                                }} testid={`story-card-${i}-title`} />
                                <button onClick={() => set("story.food_cards", data.story.food_cards.filter((_, idx) => idx !== i))} className="inline-flex items-center gap-1 text-destructive text-[10px] tracked">
                                    <Trash2 size={12} /> Remove
                                </button>
                            </div>
                        </div>
                    ))}
                    <button onClick={() => set("story.food_cards", [...(data.story.food_cards || []), { image: "", label: "", title: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Food Card
                    </button>
                </div>
            </SectionCard>

            {/* COFFEE */}
            <SectionCard title="Our Coffee" description="3 signature coffee cards.">
                <TextField label="Title" value={data.coffee.title} onChange={(v) => set("coffee.title", v)} testid="coffee-title" />
                {data.coffee.items?.map((c, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 p-4 border border-coffee/10">
                        <ImagePicker value={c.image} onChange={(v) => {
                            const arr = [...data.coffee.items]; arr[i] = { ...arr[i], image: v };
                            set("coffee.items", arr);
                        }} testid={`coffee-${i}-image`} hint="Recommended: 800 × 800 px (square) · JPG · under 400 KB" />
                        <div className="space-y-4">
                            <TextField label="Name" value={c.name} onChange={(v) => {
                                const arr = [...data.coffee.items]; arr[i] = { ...arr[i], name: v };
                                set("coffee.items", arr);
                            }} testid={`coffee-${i}-name`} />
                            <TextArea label="Descriptor" rows={2} value={c.descriptor} onChange={(v) => {
                                const arr = [...data.coffee.items]; arr[i] = { ...arr[i], descriptor: v };
                                set("coffee.items", arr);
                            }} testid={`coffee-${i}-descriptor`} />
                            <button onClick={() => set("coffee.items", data.coffee.items.filter((_, idx) => idx !== i))} className="inline-flex items-center gap-1 text-destructive text-[10px] tracked">
                                <Trash2 size={12} /> Remove
                            </button>
                        </div>
                    </div>
                ))}
                <button onClick={() => set("coffee.items", [...(data.coffee.items || []), { image: "", name: "", descriptor: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                    <Plus size={14} /> Add Coffee Item
                </button>
                <TextField label="Teaser" value={data.coffee.teaser} onChange={(v) => set("coffee.teaser", v)} testid="coffee-teaser" />
                <TextField label="CTA Label" value={data.coffee.cta_label} onChange={(v) => set("coffee.cta_label", v)} testid="coffee-cta-label" />
                <TextField label="CTA Link" value={data.coffee.cta_link} onChange={(v) => set("coffee.cta_link", v)} testid="coffee-cta-link" />
            </SectionCard>

            {/* FOOD */}
            <SectionCard title="Our Food" description="6 food category cards + description shown on Home.">
                <TextField label="Section Title" value={data.food.title} onChange={(v) => set("food.title", v)} testid="food-title" />
                <TextField label="Background Watermark Text" value={data.food.watermark} onChange={(v) => set("food.watermark", v)} testid="food-watermark" />
                <TextArea label="Description Paragraph" rows={4} value={data.food.description} onChange={(v) => set("food.description", v)} testid="food-description" />

                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Category Cards (6 recommended)</div>
                    {(data.food.categories || []).map((cat, i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 p-4 border border-coffee/10">
                            <ImagePicker
                                label={`Card ${i + 1} — Image`}
                                value={cat.image}
                                onChange={(v) => {
                                    const arr = [...data.food.categories]; arr[i] = { ...arr[i], image: v };
                                    set("food.categories", arr);
                                }}
                                testid={`food-cat-${i}-image`}
                                aspect="aspect-[5/3]"
                                hint="Recommended: 900 × 540 px (5:3 landscape) · JPG · under 400 KB"
                            />
                            <div className="space-y-4">
                                <TextField label="Title" value={cat.title} onChange={(v) => {
                                    const arr = [...data.food.categories]; arr[i] = { ...arr[i], title: v };
                                    set("food.categories", arr);
                                }} testid={`food-cat-${i}-title`} />
                                <TextField label="Note" value={cat.note} onChange={(v) => {
                                    const arr = [...data.food.categories]; arr[i] = { ...arr[i], note: v };
                                    set("food.categories", arr);
                                }} testid={`food-cat-${i}-note`} />
                                <button onClick={() => set("food.categories", data.food.categories.filter((_, idx) => idx !== i))} className="inline-flex items-center gap-1 text-destructive text-[10px] tracked">
                                    <Trash2 size={12} /> Remove
                                </button>
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={() => set("food.categories", [...(data.food.categories || []), { image: "", title: "", note: "" }])}
                        className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline"
                    >
                        <Plus size={14} /> Add Category Card
                    </button>
                </div>
            </SectionCard>

            {/* COMMUNITY */}
            <SectionCard title="The Hideout Commune" description="6 commune cards (Cricket, Music, Laugh, Book, Art, Social) + CTA banner.">

                <div>
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Commune Cards (6 cards recommended — 3 + 3 grid)</div>
                    {(data.community.communes || []).map((c, i) => (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 p-4 border border-coffee/10">
                            <ImagePicker
                                label={`Card ${i + 1} — Image`}
                                value={c.image}
                                onChange={(v) => {
                                    const arr = [...data.community.communes]; arr[i] = { ...arr[i], image: v };
                                    set("community.communes", arr);
                                }}
                                testid={`commune-${i}-image`}
                                aspect="aspect-[5/4]"
                                hint="Recommended: 900 × 720 px (5:4 landscape) · JPG · under 500 KB"
                            />
                            <div className="space-y-3">
                                <TextField label="Name" value={c.name} onChange={(v) => {
                                    const arr = [...data.community.communes]; arr[i] = { ...arr[i], name: v };
                                    set("community.communes", arr);
                                }} testid={`commune-${i}-name`} />
                                <TextField label="Day / Time Tag" value={c.tag} onChange={(v) => {
                                    const arr = [...data.community.communes]; arr[i] = { ...arr[i], tag: v };
                                    set("community.communes", arr);
                                }} testid={`commune-${i}-tag`} />
                                <TextField label="Doodle Icon (cricket, music, laugh, book, art, social)" value={c.doodle} onChange={(v) => {
                                    const arr = [...data.community.communes]; arr[i] = { ...arr[i], doodle: v };
                                    set("community.communes", arr);
                                }} testid={`commune-${i}-doodle`} />
                                <TextArea label="Note" rows={3} value={c.note} onChange={(v) => {
                                    const arr = [...data.community.communes]; arr[i] = { ...arr[i], note: v };
                                    set("community.communes", arr);
                                }} testid={`commune-${i}-note`} />
                                <button onClick={() => set("community.communes", data.community.communes.filter((_, idx) => idx !== i))} className="inline-flex items-center gap-1 text-destructive text-[10px] tracked">
                                    <Trash2 size={12} /> Remove
                                </button>
                            </div>
                        </div>
                    ))}
                    <button onClick={() => set("community.communes", [...(data.community.communes || []), { image: "", name: "", tag: "", note: "", doodle: "scribble" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Commune Card
                    </button>
                </div>

                <div className="border-t border-coffee/10 pt-6 space-y-4">
                    <div className="tracked text-coffee/60 text-[10px]">CTA Banner</div>
                    <TextField label="Label" value={data.community.cta_banner?.label} onChange={(v) => set("community.cta_banner", { ...data.community.cta_banner, label: v })} testid="community-cta-label" />
                    <TextField label="Link" value={data.community.cta_banner?.link} onChange={(v) => set("community.cta_banner", { ...data.community.cta_banner, link: v })} testid="community-cta-link" />
                </div>
            </SectionCard>

            {/* INSTAGRAM */}
            <SectionCard title="Instagram Grid" description="4×4 grid of 16 square tiles. Upload your favorite IG posts as images here — they show on the Home page.">
                <TextField label="Handle" value={data.instagram.handle} onChange={(v) => set("instagram.handle", v)} testid="ig-handle" />
                <TextField label="CTA Label" value={data.instagram.cta_label} onChange={(v) => set("instagram.cta_label", v)} testid="ig-cta-label" />
                <TextField label="CTA Link" value={data.instagram.cta_link} onChange={(v) => set("instagram.cta_link", v)} testid="ig-cta-link" />

                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-3">Tiles (16 recommended for 4×4 grid)</div>
                    <div className="grid grid-cols-3 gap-3">
                    {(data.instagram.tiles || []).map((url, i) => (
                        <div key={i}>
                            <ImagePicker value={url} onChange={(v) => {
                                const arr = [...data.instagram.tiles]; arr[i] = v;
                                set("instagram.tiles", arr);
                            }} testid={`ig-tile-${i}`} aspect="aspect-square" hint="Recommended: 600 × 600 px (square) · JPG · under 200 KB" />
                            <button onClick={() => set("instagram.tiles", data.instagram.tiles.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked mt-1">
                                <Trash2 size={11} className="inline" /> Remove
                            </button>
                        </div>
                    ))}
                </div>
                <button onClick={() => set("instagram.tiles", [...(data.instagram.tiles || []), ""])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                    <Plus size={14} /> Add Tile
                </button>
                </div>
            </SectionCard>

            {/* FOOTER */}
            <SectionCard title="Footer & Newsletter" description="Tagline, social, reach us, newsletter copy.">
                <TextField label="Tagline" value={data.footer.tagline} onChange={(v) => set("footer.tagline", v)} testid="footer-tagline" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <TextField label="Instagram URL" value={data.footer.social?.instagram} onChange={(v) => set("footer.social", { ...data.footer.social, instagram: v })} testid="footer-ig" />
                    <TextField label="LinkedIn URL" value={data.footer.social?.linkedin} onChange={(v) => set("footer.social", { ...data.footer.social, linkedin: v })} testid="footer-li" />
                    <TextField label="YouTube URL" value={data.footer.social?.youtube} onChange={(v) => set("footer.social", { ...data.footer.social, youtube: v })} testid="footer-yt" />
                </div>
                <TextField label="Reach Us — Email" value={data.footer.reach_us?.email} onChange={(v) => set("footer.reach_us", { ...data.footer.reach_us, email: v })} testid="footer-email" />
                <TextField label="Reach Us — Location" value={data.footer.reach_us?.location} onChange={(v) => set("footer.reach_us", { ...data.footer.reach_us, location: v })} testid="footer-location" />
                <TextField label="Newsletter Label" value={data.footer.newsletter?.label} onChange={(v) => set("footer.newsletter", { ...data.footer.newsletter, label: v })} testid="footer-news-label" />
            </SectionCard>

            <SaveBar onSave={saveAll} saving={saving} label="Save Home Page" />
        </div>
    );
}
